import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useGetUserByEmail } from "@src/appV2/User/api/useGetUserByEmail";
import constate from "constate";
import { minutesToMilliseconds } from "date-fns";
import { useCallback, useRef } from "react";

const FIVE_MINUTES = 1000 * 60 * 5;

interface UseKnockTokenOptions {
  userId: string | undefined;
}

function useKnockToken({ userId }: UseKnockTokenOptions) {
  const refreshPromiseRef = useRef<Promise<string | undefined>>();
  const lastRefreshAttemptAtRef = useRef<number>(0);

  const {
    data: user,
    refetch: refetchUser,
    isLoading,
  } = useGetUserByEmail({
    enabled: isDefined(userId),
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleKnockTokenExpiring = useCallback(async () => {
    if (refreshPromiseRef.current) {
      logEvent(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_REQUESTED, {
        userId,
        hasRecentAttempt: false,
        hasPerformedRefresh: false,
      });
      return await refreshPromiseRef.current;
    }

    const hasRecentAttempt =
      Date.now() - lastRefreshAttemptAtRef.current < minutesToMilliseconds(1);

    if (hasRecentAttempt) {
      logEvent(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_REQUESTED, {
        userId,
        hasRecentAttempt: true,
        hasPerformedRefresh: false,
      });
      return user?.knockToken;
    }

    lastRefreshAttemptAtRef.current = Date.now();

    const refreshPromise = (async () => {
      try {
        const { data } = await refetchUser();
        logEvent(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_REQUESTED, {
          userId,
          hasRecentAttempt: false,
          hasPerformedRefresh: true,
        });
        return data?.knockToken;
      } catch (error) {
        logError(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_FAILED, { error });
        return undefined;
      } finally {
        refreshPromiseRef.current = undefined;
      }
    })();

    refreshPromiseRef.current = refreshPromise;

    return await refreshPromise;
  }, [refetchUser, user?.knockToken, userId]);

  return {
    knockToken: user?.knockToken,
    isLoading,
    handleKnockTokenExpiring,
  };
}

export const [KnockTokenProvider, useKnockTokenContext] = constate(useKnockToken);
